// ======================================================================
// File Name       : ContentHistoryAggregateByProblem
//
// Copyright(C) 2017 NIPPON TELEGRAPH AND TELEPHONE EAST CORPORATION All
// Rights Reserved.
//
// This source code or any portion thereof must not be
// reproduced or used in any manner whatsoever.
// ======================================================================

import _ from 'lodash';
import React from 'react';
import { Col, Form } from 'react-bootstrap';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Surface, Symbols } from 'recharts';
import { MAX_CHARACTERS_DISPLAY_ON_AGGREGATE_BY_PROBLEM, MAX_QUESTION_DISPLAY_GOBACK_LINK } from '../../../constants/ContentHistoryConstant';
import BackButton from "../../BackButton";
import CustomLink from '../../button/CustomLink';

const ContentHistoryAggregateByProblem = ({
  goBack,
  itemDetail,
  questionAnswerSummaryList,
}) => {
  const COLORS = [
    '#4F81BD',
    '#C0504D',
    '#9BBB59',
    '#8064A2',
    '#4BACC6',
    '#F79646',
    '#2C4D75',
    '#772C2A',
    '#5F7530',
    '#4D3B62',
    '#276A7C',
  ];
  const RADIAN = Math.PI / 180;

  // Custom Pie Label
  const renderCustomizedPieLabel = ({ cx, cy, midAngle, outerRadius, value, percent, fill, index }) => {
    const radius = outerRadius * 1.57;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    if (value > 0) {
      return (
        <text
          x={x}
          y={y} 
          fill={fill}
          textAnchor={'middle'}
          dominantBaseline="central"
        >
         {`${(percent * 100).toFixed(0)}%`}
        </text>
      );
    }
    return <text />
  };

  const renderCustomizedPieLegend = ({ payload }) => {
    return (
      <div>
        {payload.map((entry, index) => {
          const { dataKey, data } = entry;
          const { name, highlightFlag } = data;
          return (
            <div key={dataKey} className={highlightFlag ? 'margin-bottom-10 bold' : 'margin-bottom-10'}>
              <Surface width={10} height={10}>
                <Symbols
                  cx={5}
                  cy={5}
                  type="square"
                  size={50}
                  fill={COLORS[index % COLORS.length]}
                />
              </Surface>
              <span key={dataKey} className={highlightFlag ? 'margin-left-10 textColorRed' : 'margin-left-10'}>{name}</span>
            </div>
          );
        })}
      </div>
    );
  };

  let isShowEmptyPieChart = false;
  function calculatePieChartData(topAnswerContentList) {
    let pieChartData = [];
    if (!topAnswerContentList || topAnswerContentList.length === 0) {
      pieChartData.push({
        name: '',
        value: 1,
        highlightFlag: false,
      });
      isShowEmptyPieChart = true;
      return pieChartData;
    }

    topAnswerContentList.map((entry, index) => {
      let decodedAnswerContent = decodeURIComponent(entry.answerContent);
      if (decodedAnswerContent.length > MAX_CHARACTERS_DISPLAY_ON_AGGREGATE_BY_PROBLEM) {
        decodedAnswerContent = decodedAnswerContent.slice(0, MAX_CHARACTERS_DISPLAY_ON_AGGREGATE_BY_PROBLEM).concat('...');
      }

      pieChartData.push({
        name: `${decodedAnswerContent}（${entry.userCount}）　`,
        value: entry.userCount,
        highlightFlag: entry.highlightFlag,
      });
    });
    return pieChartData;
  }

  const questionAnswerSummaryListForRender = questionAnswerSummaryList && questionAnswerSummaryList.map((entry, index) => {
    const pieChartData = calculatePieChartData(entry.topAnswerContentList);

    return (
      <Form.Group className="form-info" key={`${entry.questionSecernId}-${index}`}>
        <Col sm={3} className="form-label bold">
          {`問題 ${index + 1}`}
        </Col>
        <Col sm={12} style={{ border: '1px solid #D9D9D9', width: '95%', marginLeft: '10px', marginBottom: '10px' }} />
        <Col sm={12} className="form-label margin-left-10">
          <Col sm={3}>問題識別ID</Col>
          <Col sm={7}>{entry.questionSecernId}</Col>
        </Col>
        {/* #7428 START */}
        {/* #7458 START */}
        {entry.correctAnswerPercentage !== null && (
          <Col sm={12} className="form-label margin-left-10">
            <Col sm={3}>正答率</Col>
            <Col sm={7}>{`${entry.correctAnswerPercentage}%`}</Col>
          </Col>
        )}
        {/* #7458 END */}
        {/* #7428 END */}
        <Col sm={12} className="padding-left-right-clear margin-top-10">
          <Col sm={3} className="padding-left-right-clear">
            <ResponsiveContainer height={300} width="100%" >
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  label={isShowEmptyPieChart ? '' : renderCustomizedPieLabel}
                  outerRadius={65}
                  dataKey="value"
                  isAnimationActive={false}
                  paddingAngle={isShowEmptyPieChart ? 0: 1}
                  startAngle={isShowEmptyPieChart ? undefined: 90}
                  endAngle={isShowEmptyPieChart ? undefined: -270}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={isShowEmptyPieChart ? '#F2F2F2' : COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </Col>
          <Col sm={7}>
            {!isShowEmptyPieChart && (
              <Legend
                layout="vertical"
                verticalAlign="middle"
                align="right"
                wrapperStyle={{
                  position: 'absolute',
                  top: 'auto',
                  right: 'auto',
                  height: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '10px'
                }}
                payload={_.toPairs(pieChartData).map(pair => ({
                  dataKey: pair[0],
                  data: pair[1]
                }))}
                content={renderCustomizedPieLegend}
              />
            )}
          </Col>
        </Col>
      </Form.Group>
    );
  });

  const isShowGoBackLink = questionAnswerSummaryList && questionAnswerSummaryList.length > MAX_QUESTION_DISPLAY_GOBACK_LINK;

  return (
    <div>
      <Col sm={12}>
        <Form className='form-horizontal'>
          <Form.Group className="form-info">
            <Col sm={3} className="form-label margin-left-10">
              コンテンツ識別ID
            </Col>
            <Col sm={7} className="padding-top-bottom-10">
              {itemDetail && itemDetail.contentInfo.contentSecernId}
            </Col>
          </Form.Group>
          <Form.Group className="form-info">
            <Col sm={3} className="form-label margin-left-10">
              コンテンツ名
            </Col>
            <Col sm={7} className="padding-top-bottom-10">
              {itemDetail && itemDetail.contentInfo.contentName}
            </Col>
          </Form.Group>
            {questionAnswerSummaryListForRender}
        </Form>
      </Col>
      {isShowGoBackLink && (
        <div className="margin-top-30 margin-bottom-20 col-sm-12 padding-left-clear">
          <CustomLink
            className="margin-right-7 url-list"
            text="コンテンツ別履歴一覧に戻る"
            icon="bi bi-chevron-left"
            onClick={goBack}
          />
        </div>
      )
      }
    </div>
  );
};

export default ContentHistoryAggregateByProblem;
